import { Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type Theme = 'light' | 'dark';

export interface ChartTheme {
  background: string;
  labelColor: string;
  titleColor: string;
  axisColor: string;
  gridColor: string;
  tooltipBackground: string;
  tooltipColor: string;
  seriesColors: string[];
}

const THEME_STORAGE_KEY = 'forecastrx-theme';
const THEME_ATTRIBUTE = 'data-theme';
const DEFAULT_THEME: Theme = 'light';

const COLOR_BG_PAGE = '--color-bg-page';
const COLOR_BG_ELEVATED = '--color-bg-elevated';
const COLOR_TEXT_PRIMARY = '--color-text-primary';
const COLOR_TEXT_HEADING = '--color-text-heading';
const COLOR_BORDER = '--color-border';
const COLOR_BORDER_SUBTLE = '--color-border-subtle';

const CHART_SERIES_TOKENS = [
  '--color-chart-series-1',
  '--color-chart-series-2',
  '--color-chart-series-3',
  '--color-chart-series-4',
  '--color-chart-series-5',
  '--color-chart-series-6',
  '--color-chart-series-7',
  '--color-chart-series-8',
];

// Static fallback so chartTheme$ has a usable value before the stylesheet
// has been parsed (e.g. on first emit during app bootstrap, before the
// rAF that re-reads computed CSS vars fires). Mirrors the light :root
// values defined in scss/tokens/base/*.scss.
const FALLBACK_CHART_THEME: ChartTheme = {
  background: '#ffffff',
  labelColor: '#222222',
  titleColor: '#222222',
  axisColor: '#e3eaef',
  gridColor: '#eceff1',
  tooltipBackground: '#ffffff',
  tooltipColor: '#222222',
  seriesColors: [
    '#4099ff',
    '#22c55e',
    '#fd7e14',
    '#7759de',
    '#00bcd4',
    '#e83e8c',
    '#ffb64d',
    '#ff5370',
  ],
};

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private readonly renderer: Renderer2;
  private readonly themeSubject: BehaviorSubject<Theme>;
  private readonly chartThemeSubject: BehaviorSubject<ChartTheme>;

  readonly theme$: Observable<Theme>;
  readonly chartTheme$: Observable<ChartTheme>;

  constructor(rendererFactory: RendererFactory2) {
    this.renderer = rendererFactory.createRenderer(null, null);
    this.themeSubject = new BehaviorSubject<Theme>(this.readPersistedTheme());
    this.theme$ = this.themeSubject.asObservable();
    this.applyTheme(this.currentTheme);

    this.chartThemeSubject = new BehaviorSubject<ChartTheme>(FALLBACK_CHART_THEME);
    this.chartTheme$ = this.chartThemeSubject.asObservable();
    this.scheduleChartThemeRefresh();
  }

  get currentTheme(): Theme {
    return this.themeSubject.value;
  }

  setTheme(theme: Theme): void {
    // 1. Temporarily disable transitions during theme application
    this.renderer.addClass(document.documentElement, 'theme-switching');

    this.applyTheme(theme);
    this.persistTheme(theme);
    this.themeSubject.next(theme);
    this.scheduleChartThemeRefresh();

    // 2. Force a layout reflow to ensure style changes apply immediately
    document.documentElement.offsetHeight;

    // 3. Remove the class on the next frame to restore regular transitions
    requestAnimationFrame(() => {
      this.renderer.removeClass(document.documentElement, 'theme-switching');
    });
  }

  applyTemporaryTheme(theme: Theme): void {
    this.applyTheme(theme);
    this.themeSubject.next(theme);
    this.scheduleChartThemeRefresh();
  }

  toggle(): void {
    this.setTheme(this.currentTheme === 'light' ? 'dark' : 'light');
  }

  private applyTheme(theme: Theme): void {
    this.renderer.setAttribute(document.documentElement, THEME_ATTRIBUTE, theme);
  }

  private persistTheme(theme: Theme): void {
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch {}
  }

  private readPersistedTheme(): Theme {
    let storedTheme: string | null;

    try {
      storedTheme = localStorage.getItem(THEME_STORAGE_KEY);
    } catch {
      return DEFAULT_THEME;
    }

    return this.isTheme(storedTheme) ? storedTheme : DEFAULT_THEME;
  }

  private isTheme(theme: string | null): theme is Theme {
    return theme === 'light' || theme === 'dark';
  }

  // CSS custom properties must be read AFTER the browser has applied the
  // updated [data-theme] attribute and parsed the global stylesheet.
  // requestAnimationFrame fires before the next paint, after style recalc,
  // which guarantees getComputedStyle returns the active theme values.
  private scheduleChartThemeRefresh(): void {
    if (typeof requestAnimationFrame === 'undefined') {
      this.emitChartTheme();
      return;
    }
    requestAnimationFrame(() => this.emitChartTheme());
  }

  private emitChartTheme(): void {
    const next = this.createChartTheme();
    if (!this.isChartThemeUsable(next)) {
      return;
    }
    this.chartThemeSubject.next(next);
  }

  private createChartTheme(): ChartTheme {
    const style = getComputedStyle(document.documentElement);
    return {
      background: this.getCssVariable(COLOR_BG_PAGE, style),
      labelColor: this.getCssVariable(COLOR_TEXT_PRIMARY, style),
      titleColor: this.getCssVariable(COLOR_TEXT_HEADING, style),
      axisColor: this.getCssVariable(COLOR_BORDER, style),
      gridColor: this.getCssVariable(COLOR_BORDER_SUBTLE, style),
      tooltipBackground: this.getCssVariable(COLOR_BG_ELEVATED, style),
      tooltipColor: this.getCssVariable(COLOR_TEXT_PRIMARY, style),
      seriesColors: CHART_SERIES_TOKENS.map((token) => this.getCssVariable(token, style)),
    };
  }

  // Reject ONLY when the stylesheet returned no usable values at all
  // (e.g. ThemeService instantiated before bundle.css parsed). If any
  // canonical field resolved, treat the read as successful so subsequent
  // toggles can update — otherwise the fallback would be sticky forever.
  private isChartThemeUsable(theme: ChartTheme): boolean {
    return Boolean(theme.labelColor);
  }

  getCssVariable(name: string, style?: CSSStyleDeclaration): string {
    const computed = style || getComputedStyle(document.documentElement);
    return computed.getPropertyValue(name).trim();
  }
}
